// Per-device localStorage persistence for genuinely device-local, in-progress
// UI state (held sales, cash drawer close-outs, which store/register this
// till is paired to). None of this is a synced business record — contrast
// with orders/order-items, which always go through the real collections.
import type { DrawerRecord, HeldOrder, PairedDevice, PosPreferences } from "../types";

const DEVICE_KEY = "cliqsuite_pos_device_v1";
const HELD_KEY = "cliqsuite_pos_held_v1";
const DRAWER_KEY = "cliqsuite_pos_drawer_v1";
const PREFS_KEY = "cliqsuite_pos_prefs_v1";

function readJson<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota/availability errors */
  }
}

export const readPairedDevice = () => readJson<PairedDevice>(DEVICE_KEY);
export const writePairedDevice = (device: PairedDevice | null) => writeJson(DEVICE_KEY, device);

export const readHeldOrders = () => readJson<HeldOrder[]>(HELD_KEY) ?? [];
export const writeHeldOrders = (list: HeldOrder[]) => writeJson(HELD_KEY, list);

export const readDrawer = () => readJson<DrawerRecord>(DRAWER_KEY);
export const writeDrawer = (record: DrawerRecord | null) => writeJson(DRAWER_KEY, record);

const DEFAULT_PREFS: PosPreferences = { menuPhotos: true, notif: true, reduceAnimation: false };
export const readPosPreferences = (): PosPreferences => ({ ...DEFAULT_PREFS, ...readJson<PosPreferences>(PREFS_KEY) });
export const writePosPreferences = (prefs: PosPreferences) => writeJson(PREFS_KEY, prefs);

// Ticket labels ("#12") for open tickets before the server assigns the real order number.
// Counts per device per day; purely a label, never sent to the server.
const TICKET_SEQ_KEY = "cliqsuite_pos_ticket_seq_v1";
export function nextTicketNo(): string {
  const today = new Date().toDateString();
  const seq = readJson<{ day: string; n: number }>(TICKET_SEQ_KEY);
  const n = seq?.day === today ? seq.n + 1 : 1;
  writeJson(TICKET_SEQ_KEY, { day: today, n });
  return String(n);
}
